import { Button, Divider, Typography } from '@material-ui/core';
import Spacing from '../../portico/spacing/spacing';
import AccordionPanel from '../../portico/accordion/accordion-panel';
import React, { useEffect, useState } from 'react';

import CloseIcon from '../../../assets/teal-blue-circular-close.svg';
import ContentWrapper from '../../../content-wrapper/content-wrapper';
import { JourneyDocument } from '../../../interfaces/JourneyDocument.interface';
import { IRequirementViewModel } from '../../../interfaces/RequirementsInScopeModel';
import { evaluatePolicyEngineDataGroup } from '../../../services/fenergo-service';
import { useEntityState } from '../../../stores/entity/entity.hooks';
import {useCurrentStepperIndex} from '../../../stores/ui/ui.hooks';
import CONSTANTS from './../../../utils/fenergo-constants';
import useFormDataGroupsStyles from './form-data-groups.styles'
import { renderDataField, renderFieldLabel } from './form-fields-renderer';
import useFormFieldRendererStyles from './form-fields-renderer.styles';
import {
  addAnotherDataGroup, canDisplayField, getDataGroupExpirationDate, getDynamicDataGroupFieldsReqPayload,
  getDynamicProps,
  getFormDataValuesObject, getTooltipDescription, getValidFieldContentKey, isOutlinedLabel,
  loadFormFields,
  parseMetaDataAttribute,
  removeDataGroupAndUpdateFormValues
} from './form-helper-methods';


interface FormDataGroupsProps {
  formMethods?: any
  onChange: any;

  initialValues: any;
  documents: JourneyDocument[]
  journeyId: string;
  entityId: string;
  entityType?: string;
  description?: string;
  lookups: Array<object>;
  dataGroupName: string;
  dataGroupId: string
  dataGroupTitle: string;
  contentKey: string;

  requirementsInScope: IRequirementViewModel[];
  dynamicDataGroupRequirements: any;
  lookupList: Array<object>;
  lookupLinks: Array<object>;
  dataGroupsSettings: Array<object>;

  loading: boolean;
  readOnly?: boolean;
  labelInsteadOfControl?: boolean;
  collapsedByDefault?: boolean;
  uiMetaData?: any;
}


export const FormDataGroups: React.FC<FormDataGroupsProps> = ({
  dataGroupName, dataGroupId, dataGroupTitle,
  onChange, initialValues, documents,
  journeyId, entityId,
  requirementsInScope, dynamicDataGroupRequirements,
  lookupList, lookupLinks, dataGroupsSettings,
  loading, readOnly, labelInsteadOfControl, collapsedByDefault, lookups, description,
  contentKey, uiMetaData, formMethods
}) => {
  const [panels, setPanels] = useState<Array<string>>(null);
  const [fieldsInPanel, setFieldsInPanel] = useState<object>(null);
  const [currentValues, setCurrentValues] = useState<object>(initialValues);
  const [dynamicDataGroupFields, setDynamicDataGroupFields] = useState<Array<Array<any>>>(null);
  const { entityDraft, dynamicJurisdictions } = useEntityState();
  const stepperIndex = useCurrentStepperIndex();
  const [optionalDataGroupListAddedManually, setOptionalDataGroupListAddedManually] = useState<Array<string>>([]);
  const [removedDataGroupIndexes, setRemovedDataGroupIndexes] = useState<object>({});

  const classes = useFormDataGroupsStyles();
  const isSingleCardinality = uiMetaData?.multiCardinality === 'false';
  const formFieldRendererStyles = useFormFieldRendererStyles();
  readOnly = (stepperIndex === 6 || readOnly)
  useEffect(() => {
    if (loading || !requirementsInScope || requirementsInScope.length === 0) return;

    loadFormFields(initialValues, formMethods, requirementsInScope, setFieldsInPanel, setPanels);

    return () => {};

  }, [loading, lookupList, requirementsInScope, dataGroupsSettings]);

  useEffect(() => {
    if (panels) {
      for (const panel of panels) {
        const fields = getFieldModels(panel);

        for (const field of fields) {
          if (field) {
            onDataGroupFieldChange(field);
          }
        }
      }
    }
  }, [panels]);

  const onFieldChange = (dataGroupField: IRequirementViewModel) => {
    return () => {
      onDataGroupFieldChange(dataGroupField);
      onChange();
    };
  };

  const getDynamicDataGroupFields = async (dataGroupField: IRequirementViewModel) => {
    const latestValues = Object.assign({}, initialValues, getFormDataValuesObject(formMethods.getValues()));
    const dynamicDataGroupRequests = getDynamicDataGroupFieldsReqPayload(latestValues, dataGroupsSettings, dataGroupId, dataGroupField);

    // This is neccessary for if the field is blank and adding a new group.
    // evaluatePolicyPromises needs to have some content in order to add a new one
    if (dynamicDataGroupRequests.length === 0) {
      return [[]];
    }
    if (!dynamicDataGroupRequests) return;

    const evaluatePolicyPromises = await Promise.all(dynamicDataGroupRequests.map((data => {
      return evaluatePolicyEngineDataGroup(data.groupId, data.properties);
    })));

    return evaluatePolicyPromises;
  };

  const onDataGroupFieldChange = async (dataGroupField: IRequirementViewModel) => {
    const evaluatePolicyPromises = await getDynamicDataGroupFields(dataGroupField);
    if (evaluatePolicyPromises.length > 0) setDynamicDataGroupFields(evaluatePolicyPromises);
  };

  function getFieldModels(panelName: string) {
    const fieldsToRender: string[] = Object.keys(fieldsInPanel).filter((key) => { return fieldsInPanel[key] === panelName; });

    return requirementsInScope
      .filter((field) => { return !dataGroupName || (dataGroupName === field.dataField.propertyName); })
      .filter((field) => { return fieldsToRender.indexOf(field.dataField.propertyName) >= 0; })
      .filter((field) => { return field.dataField.propertyType === CONSTANTS.FIELDS.DATA_GROUP; })
      .map((field) => {
        return field;
      });
  }

  {/*
    Form Rendering Flow - Step 2. Gets all the relevant fields from the fieldsInPanel
    (which gets set using the form-fields-renderer.loadFormFields() method) if...
      1. field's propertyName matches the dataGroup's name
      2. field's propertyName exists in `fieldsToRender`
      3. field's propertyType is "data group"
    Then calls renderControl(field) on each filtered field. (Go to 3)
  */}
  const getPanelFields = (panelName: string) => {
    // Include any field whose value matches "panelName"
    const fieldsToRender: string[] = Object.keys(fieldsInPanel).filter((key) => { return fieldsInPanel[key] === panelName; });

    return requirementsInScope
      .filter((field) => { return !dataGroupName || (dataGroupName === field.dataField.propertyName); }) // include field if field's name matches data group's
      .filter((field) => { return fieldsToRender.indexOf(field.dataField.propertyName) >= 0; }) // include field if property name found in fieldsToRender
      .filter((field) => { return field.dataField.propertyType === CONSTANTS.FIELDS.DATA_GROUP; }) // include fields of type "data group"
      .map((field) => {
        return renderControl(field);
      });
  };

  {/*
    Form Rendering Flow - Step 3. Not exactly sure what the purpose of this function is, but it seems to...
    1. Find the configuration for a data group by matching `propertyTypeId` to `groupId`
    2. Checks if the field allows multiple entries (cardinality > 0)
    3. Renders either a single field group or a repeating field group based on cardinality
  */}
  const renderControl = (dataGroupField: IRequirementViewModel) => {
    if (dataGroupsSettings && dataGroupsSettings.length > 0) {
      /**
       * The settings object for the relevant IRequirementViewModel
       */
      // find settings obj where groupId matches field's propertyTypeId
      const dataGroup: any = dataGroupsSettings.find((dg: any) => dg.groupId === dataGroupField.dataField.propertyTypeId);
      // find dynDataGroup the same way as above
      const dynamicDataGroup: any = dynamicDataGroupRequirements.find((dg: any) => dg.groupId === dataGroupField.dataField.propertyTypeId);

      // if match found...
      if (dataGroup) {
        // make unique key
        const dataGroupKey = `dataGroup_${dataGroup.groupId}_${dataGroupField.dataField.propertyName}_${dataGroup.cardinality}`;

        // store how manyy instances of this group are allowed
        const dataGroupCardinality = dataGroup.cardinality;
        return (
            // if allows many entries
          dataGroupCardinality
              // render repeating group
            ? renderDataGroupFieldsWithMultipleCardinality(dataGroupKey, dataGroupField, dataGroupCardinality, dataGroup, dynamicDataGroup)
              // else render single instance
            : renderDataGroupFields(dataGroupKey, dataGroupCardinality, dataGroup, dynamicDataGroup, 0, null, readOnly)
        );
      }
    }
  };

  const renderDataGroupFieldsWithMultipleCardinality = (dataGroupKey, dataGroupField: IRequirementViewModel, dataGroupCardinality, dataGroup, dynamicDataGroup) => {
    const dataGroupLength = currentValues[dataGroupKey]?.length;
    const overallMaximumCount = dataGroupField.validationRule.validationData.collectionMaximumCount?.overallMaximumCount;

    return (
      <>
        {[...Array(dataGroupLength)].map((e, index) => (
          (renderDataGroupFields(dataGroupKey, dataGroupCardinality, dataGroup, dynamicDataGroup, index, dataGroupField, readOnly))
        ))}
        {!isSingleCardinality && !readOnly && (
          <Button
            disabled={overallMaximumCount && (overallMaximumCount === (dataGroupLength - new Set(removedDataGroupIndexes[dataGroupKey]).size))}
            onClick={async () => {
              addAnotherDataGroup(dataGroupKey, currentValues, setCurrentValues, formMethods, removedDataGroupIndexes, setRemovedDataGroupIndexes);
              await onDataGroupFieldChange(dataGroupField);
            }}
            color="primary"
          >
            Add
          </Button>
        )}

      </>
    );
  };

  const removeDataGroup = async (event) => {
    const { dataset } = event.currentTarget;
    removeDataGroupAndUpdateFormValues(dataset.datagroupkey, dataset.index, removedDataGroupIndexes, setRemovedDataGroupIndexes, currentValues, setCurrentValues);
    await onDataGroupFieldChange(null);
  };

  {/*
    Form Rendering Flow - Step 4. Also kind of confusing, but basically takes a data group and filters for group members (fields) based on
     some requirements. Then returns an element with a list of field elements. It also does a bunch of "rendering" or
     processing of each field to determine things like should it be displayed, does it have an outline, etc.
     Confusingly, the individual fields render function (renderDataField) is in form-fields-renderer. (See 5 below)
  */}
  const renderDataGroupFields = (dataGroupKey, dataGroupCardinality, dataGroup, dynamicDataGroup, groupIndex = 0, dataGroupField = null, readOnly) => {
    const formValues = Object.assign({}, initialValues);

    let fields = dataGroup.dataGroupFields;

    fields = fields.filter(field => field.conditions.length === 0);
    if (dynamicDataGroupFields && dynamicDataGroupFields[groupIndex]) {
      fields = fields.concat(dynamicDataGroupFields[groupIndex]);
    } else {
      fields = fields.concat(dynamicDataGroup?.dataGroupFields || []);
    }

    const { errors } = formMethods;

    fields = fields
      .sort((req1, req2) => { return req1.order - req2.order; })
      .filter(field => canDisplayField(field, formValues, dataGroupKey, groupIndex));

    const removedIndexes = removedDataGroupIndexes[dataGroupKey] || [];

    return (
      <div key={`${dataGroupKey}-${groupIndex}`}>
        {
          (!dataGroupCardinality || !removedIndexes.includes(groupIndex)) && (
            <>
              {dataGroupCardinality && groupIndex > 0 && [...Array(groupIndex).keys()].find(k => !removedIndexes.includes(k)) >= 0 && <Divider style={{ marginTop: 0, marginBottom: 22 }} />}
              {dataGroupCardinality && fields.length > 1 && groupIndex > 0 &&
                (
                  <div className={classes.dataGroupWithMultiFieldsContainer}>
                    {groupIndex === 0 && <h1>{dataGroupField.name}</h1>}
                    {!readOnly && <Button variant='outlined' color='primary' data-index={groupIndex} data-dataGroupKey={dataGroupKey} onClick={removeDataGroup}>Remove</Button>}
                  </div>
                )}
              {
                fields.map((field, index) => {
                  const propName = field.dataField?.propertyName;

                  const dataGroupFieldName = dataGroupCardinality
                    ? `${dataGroupKey}[${groupIndex}].${propName}`
                    : `${dataGroupKey}_${propName}`;

                  let dataGroupFieldValue = dataGroupCardinality
                    ? (formValues[dataGroupKey] && formValues[dataGroupKey][groupIndex]
                      && formValues[dataGroupKey][groupIndex][propName])
                    : formValues[dataGroupFieldName];

                  dataGroupFieldValue = dataGroupFieldValue ? dataGroupFieldValue : formValues[dataGroupFieldName];

                  delete errors[field.identifier];
                  if (errors[dataGroupKey] && errors[dataGroupKey][groupIndex] && errors[dataGroupKey][groupIndex][propName]) {
                    errors[field.identifier] = errors[dataGroupKey][groupIndex][propName];
                  }
                  const uiMetaData: any = parseMetaDataAttribute(field.classification);

                  const expirationDateBasedOnCitizenship = getDataGroupExpirationDate(lookups, dataGroupName, propName, entityDraft);
                  if (expirationDateBasedOnCitizenship) {
                    field.classification = expirationDateBasedOnCitizenship;
                  }

                  const tooltip_description = getTooltipDescription(lookups, dataGroupName, propName);
                  if (typeof tooltip_description === 'string' && tooltip_description !== '') {
                    field.description = tooltip_description;
                  }
                  return (
                    <div key={`${propName}-${index}`}>
                      {(!isOutlinedLabel(field) || readOnly) && renderFieldLabel(field, formFieldRendererStyles)}
                      {!isOutlinedLabel(field) && <Spacing variable="spacing-base" />}
                      {<ContentWrapper 
                          type='field' 
                          contentKey={getValidFieldContentKey(field.dataField?.propertyType, uiMetaData.contentType)} 
                          dynamicProps={getDynamicProps(entityDraft)} 
                          dynamicJurisdictions={dynamicJurisdictions}
                          {...{ children: 
                            <div className={readOnly ? null : classes.dataGroupWithSingleFieldContainer}>
                            {
                              // Form Rendering Flow - Step 5. Follow this function to form-fields-renderer...
                              renderDataField(
                                dataGroupFieldName, field, dataGroupFieldValue,
                                formMethods, onFieldChange(dataGroupField),
                                loading, readOnly, labelInsteadOfControl,
                                journeyId, entityId,
                                requirementsInScope, dynamicDataGroupRequirements,
                                lookupList, lookupLinks, dataGroupsSettings,
                                initialValues, documents, fields, lookups,
                                null, null
                              )
                            }
                            {dataGroupCardinality && (fields.length === 1) && <img className={`${classes.removeImg} ${groupIndex > 0 ? '' : classes.hideRemoveImg}`} src={CloseIcon} data-index={groupIndex} data-dataGroupKey={dataGroupKey} onClick={removeDataGroup} alt='Remove' />}
                            </div>
                          } as any }
                        />}
                      {!field.isReadOnly && (!uiMetaData || !uiMetaData.hideField) && <Spacing variable="spacing-base-x3" />}
                    </div>
                  );
                })
              }
            </>
          )
        }
      </div>
    );
  };

  return (
    <>
      {panels && panels.map((panelName, index) => (
        <div key={`${panelName}-${index}`}>
          {!isSingleCardinality && !readOnly ? (
            <AccordionPanel panelName={(dataGroupTitle && !readOnly) ? dataGroupTitle : panelName} panelFields={getPanelFields(panelName)} panelDescription={<ContentWrapper type='field' contentKey={contentKey} dynamicProps={getDynamicProps(entityDraft)} dynamicJurisdictions={dynamicJurisdictions} />} />
          ) : (
            <>
              {!readOnly? (
                <>
                  {!readOnly && <Typography variant="h1">{dataGroupTitle ? dataGroupTitle : panelName}</Typography>}
                  <Spacing variable="spacing-base-x3" />
                </>
              ) : <></>}

              {/*
                Form Rendering Flow - Step 1. Start here to begin unraveling this Leviathan of code.
                This takes you another "method" above...
               */}
              {getPanelFields(panelName)}
            </>
          )}
        </div>
      ))}
    </>
  );
};